// Per-variant nutrition schema and health scoring, stored as a flat JSON blob so new fields need no migration.

export const LEVEL_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// Pickable vitamin/mineral presence — stored as an array of value tokens.
export const VITAMIN_OPTIONS = [
  { value: 'A', label: 'Vitamin A' },
  { value: 'B1', label: 'B1 (Thiamine)' },
  { value: 'B2', label: 'B2 (Riboflavin)' },
  { value: 'B3', label: 'B3 (Niacin)' },
  { value: 'B5', label: 'B5 (Pantothenic)' },
  { value: 'B6', label: 'B6' },
  { value: 'B7', label: 'B7 (Biotin)' },
  { value: 'B9', label: 'B9 (Folate)' },
  { value: 'B12', label: 'B12' },
  { value: 'C', label: 'Vitamin C' },
  { value: 'D', label: 'Vitamin D' },
  { value: 'E', label: 'Vitamin E' },
  { value: 'K', label: 'Vitamin K' },
];
export const MINERAL_OPTIONS = [
  { value: 'iron', label: 'Iron' },
  { value: 'calcium', label: 'Calcium' },
  { value: 'potassium', label: 'Potassium' },
  { value: 'magnesium', label: 'Magnesium' },
  { value: 'zinc', label: 'Zinc' },
  { value: 'sodium', label: 'Sodium' },
  { value: 'phosphorus', label: 'Phosphorus' },
  { value: 'iodine', label: 'Iodine' },
  { value: 'selenium', label: 'Selenium' },
  { value: 'copper', label: 'Copper' },
  { value: 'manganese', label: 'Manganese' },
];

const num = (key, label, unit = '') => ({ key, label, unit, type: 'num' });
const level = (key, label) => ({ key, label, type: 'level' });
const flag = (key, label) => ({ key, label, type: 'bool' });
const multi = (key, label, options) => ({ key, label, type: 'multi', options });

export const FOOD_GROUPS = [
  { key: 'basic', title: 'Basic nutrition', fields: [
    num('calories', 'Calories', 'kcal'),
    num('protein', 'Protein', 'g'),
    num('carbs', 'Carbohydrates', 'g'),
    num('fat', 'Fat', 'g'),
    num('fiber', 'Fiber', 'g'),
    num('sugar', 'Sugar', 'g'),
    num('sodium', 'Sodium / Salt', 'mg'),
  ] },
  { key: 'macros', title: 'Detailed macros', fields: [
    num('saturated_fat', 'Saturated fat', 'g'),
    num('trans_fat', 'Trans fat', 'g'),
    num('unsaturated_fat', 'Unsaturated fat', 'g'),
    num('added_sugar', 'Added sugar', 'g'),
    num('cholesterol', 'Cholesterol', 'mg'),
  ] },
  { key: 'micros', title: 'Micronutrients', fields: [
    multi('vitamins', 'Vitamins', VITAMIN_OPTIONS),
    multi('minerals', 'Minerals', MINERAL_OPTIONS),
    num('iron', 'Iron', 'mg'),
    num('calcium', 'Calcium', 'mg'),
    num('potassium', 'Potassium', 'mg'),
    num('magnesium', 'Magnesium', 'mg'),
  ] },
  { key: 'scoring', title: 'Health scoring', fields: [
    level('carb_quality', 'Carb quality'),
    level('fat_quality', 'Fat quality'),
    level('fiber_content', 'Fiber content'),
    level('sugar_level', 'Sugar level'),
    level('sodium_level', 'Sodium level'),
    level('processing_level', 'Processing level'),
    num('portion_size', 'Portion size', 'g'),
    num('glycemic_index', 'Glycemic index'),
    num('glycemic_load', 'Glycemic load'),
  ] },
  { key: 'diet', title: 'Diet-specific flags', fields: [
    flag('vegetarian', 'Vegetarian'),
    flag('vegan', 'Vegan'),
    flag('gluten_free', 'Gluten-free'),
    flag('keto_friendly', 'Keto-friendly'),
    flag('diabetic_friendly', 'Diabetic-friendly'),
    flag('high_protein', 'High-protein'),
    flag('low_carb', 'Low-carb'),
    flag('low_fat', 'Low-fat'),
    flag('low_sodium', 'Low-sodium'),
  ] },
];

export const BEVERAGE_GROUPS = [
  { key: 'basic', title: 'Basic nutrition', fields: [
    num('calories', 'Calories', 'kcal'),
    num('sugar', 'Total sugar', 'g'),
    num('added_sugar', 'Added sugar', 'g'),
    num('carbs', 'Carbohydrates', 'g'),
    num('fat', 'Fat', 'g'),
    num('protein', 'Protein', 'g'),
    num('sodium', 'Sodium', 'mg'),
  ] },
  { key: 'drink', title: 'Drink-specific', fields: [
    level('caffeine_level', 'Caffeine level'),
    num('alcohol_content', 'Alcohol content', '%'),
    level('artificial_sweeteners', 'Artificial sweeteners'),
    level('preservatives', 'Preservatives'),
    level('acidity_level', 'Acidity level'),
    level('carbonation', 'Carbonation'),
    level('hydration_value', 'Hydration value'),
    level('electrolytes', 'Electrolytes'),
    num('serving_size', 'Serving size', 'ml'),
  ] },
  { key: 'micros', title: 'Micronutrients', fields: [
    multi('vitamins', 'Vitamins', VITAMIN_OPTIONS),
    multi('minerals', 'Minerals', MINERAL_OPTIONS),
    num('calcium', 'Calcium', 'mg'),
    num('potassium', 'Potassium', 'mg'),
    num('magnesium', 'Magnesium', 'mg'),
    num('iron', 'Iron', 'mg'),
  ] },
  { key: 'scoring', title: 'Health scoring', fields: [
    level('sugar_level', 'Sugar level'),
    level('added_sugar_level', 'Added sugar level'),
    level('calorie_density', 'Calorie density'),
    level('caffeine_safety', 'Caffeine safety'),
    level('hydration_score', 'Hydration score'),
    level('artificial_level', 'Artificial ingredient level'),
    level('processing_level', 'Processing level'),
    level('sodium_level', 'Sodium level'),
    num('portion_size', 'Portion size', 'ml'),
  ] },
  { key: 'diet', title: 'Diet-specific flags', fields: [
    flag('sugar_free', 'Sugar-free'),
    flag('low_calorie', 'Low-calorie'),
    flag('caffeine_free', 'Caffeine-free'),
    flag('diabetic_friendly', 'Diabetic-friendly'),
    flag('keto_friendly', 'Keto-friendly'),
    flag('vegan', 'Vegan'),
    flag('non_alcoholic', 'Non-alcoholic'),
    flag('energy_drink', 'Energy drink'),
    flag('sports_drink', 'Sports drink'),
  ] },
];

export const groupsForType = (dishType) =>
  (dishType === 'beverage' ? BEVERAGE_GROUPS : FOOD_GROUPS);

// Which parameters the headline Health Meter is built from, shown so the rating is transparent.
export const FOOD_METER_BASIS =
  'calories, protein, carbs, fat, fiber, sugar, sodium & processing level';
export const BEVERAGE_METER_BASIS =
  'calories, sugar, added sugar, sodium, caffeine, artificial sweeteners, preservatives & hydration';

// ---- Health scoring --------------------------------------------------------

const n = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};
const lvl = (v, map) => (map[v] != null ? map[v] : 0);
const clamp = (x, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, x));

const RATINGS = [
  { min: 80, grade: 'A', label: 'Excellent', bar: 'bg-emerald-500', text: 'text-emerald-700', ring: 'border-emerald-200 bg-emerald-50' },
  { min: 65, grade: 'B', label: 'Good',      bar: 'bg-lime-500',    text: 'text-lime-700',    ring: 'border-lime-200 bg-lime-50' },
  { min: 50, grade: 'C', label: 'Fair',      bar: 'bg-amber-500',   text: 'text-amber-700',   ring: 'border-amber-200 bg-amber-50' },
  { min: 35, grade: 'D', label: 'Poor',      bar: 'bg-orange-500',  text: 'text-orange-700',  ring: 'border-orange-200 bg-orange-50' },
  { min: 0,  grade: 'E', label: 'Unhealthy', bar: 'bg-rose-500',    text: 'text-rose-700',    ring: 'border-rose-200 bg-rose-50' },
];
const ratingFor = (score) => RATINGS.find((r) => score >= r.min) || RATINGS[RATINGS.length - 1];

function scoreFood(d) {
  let s = 50;
  s += Math.min(20, n(d.protein) * 1.2);                        // protein is good
  s += Math.min(15, n(d.fiber) * 2);                            // fibre is good
  s -= Math.min(20, Math.max(0, n(d.sugar) - 5) * 1.0);         // sugar over 5g
  s -= Math.min(15, Math.max(0, n(d.fat) - 10) * 0.5);          // fat over 10g
  s -= Math.min(10, Math.max(0, n(d.carbs) - 30) * 0.1);        // carbs over 30g
  s -= Math.min(20, Math.max(0, n(d.sodium) - 200) * 0.02);     // sodium over 200mg
  s -= Math.min(15, Math.max(0, n(d.calories) - 250) * 0.02);   // calories over 250
  s += lvl(d.processing_level, { low: 10, medium: 0, high: -15 });
  return clamp(s);
}

function scoreBeverage(d) {
  let s = 60;
  s -= Math.min(30, n(d.sugar) * 1.2);                          // total sugar
  s -= Math.min(25, n(d.added_sugar) * 1.5);                    // added sugar hurts more
  s -= Math.min(15, Math.max(0, n(d.calories) - 40) * 0.05);    // calories over 40
  s -= Math.min(10, Math.max(0, n(d.sodium) - 50) * 0.01);      // sodium over 50mg
  s += lvl(d.hydration_value, { low: -5, medium: 5, high: 12 });
  s += lvl(d.caffeine_level, { low: 0, medium: -5, high: -10 });
  s += lvl(d.artificial_sweeteners, { low: 0, medium: -5, high: -10 });
  s += lvl(d.preservatives, { low: 0, medium: -4, high: -8 });
  return clamp(s);
}

// The parameters each meter reads, also deciding whether any meaningful data has been entered at all.
const FOOD_METER_KEYS = ['calories', 'protein', 'carbs', 'fat', 'fiber', 'sugar', 'sodium', 'processing_level'];
const BEVERAGE_METER_KEYS = ['calories', 'sugar', 'added_sugar', 'sodium', 'caffeine_level', 'artificial_sweeteners', 'preservatives', 'hydration_value'];

function hasMeterData(nutrition, dishType) {
  const keys = dishType === 'beverage' ? BEVERAGE_METER_KEYS : FOOD_METER_KEYS;
  return keys.some((k) => {
    const v = nutrition[k];
    if (v == null || v === '') return false;
    const x = parseFloat(v);
    if (Number.isFinite(x)) return x !== 0;     // numeric: 0 counts as no data
    return String(v).trim() !== '';             // level strings (low/medium/high)
  });
}

const NO_DATA = {
  score: null, grade: '—', label: 'No data yet', hasData: false,
  bar: 'bg-slate-300', text: 'text-slate-500', ring: 'border-slate-200 bg-slate-50',
};

// The headline meter's score and grade, returning the neutral NO_DATA state when nothing relevant is entered.
export function computeHealthScore(nutrition = {}, dishType = 'food') {
  if (!hasMeterData(nutrition, dishType)) return NO_DATA;
  const score = Math.round(dishType === 'beverage' ? scoreBeverage(nutrition) : scoreFood(nutrition));
  return { score, hasData: true, ...ratingFor(score) };
}
